/** in this Component Grievance data is displaying in the table */

import React, { useState, useEffect } from "react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Search,
  X,
  FileText,
  Image,
  Download,
  RefreshCcw,
  CircleCheckBig,
} from "lucide-react";
import { Viewicon } from "../../../Components/IconComponents";
import { getData } from "../../../Services/ProfileServices";
import {
  fetchGrievanceData,
  getbarches,
  getThemeURL,
  getallBanksByRole,
  acceptAfterEsclation,
  reopenAfterClosed,
  getAllSLA,
  getUsersByUserLevelExceptCurrent,
} from "../../../Services/EndPoints";
import { postDataWithBody } from "../../../Services/Services";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Sheet, FileText as FileTextIcon } from "lucide-react";
import ExpandableTablegrievancetable from "./ExpandableTablegrievancetable";
import Loader from "react-spinner-loader"; // Add this import

const GrievanceTable = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false); // Add loader state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userProfile, setUserProfile] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Status - All");
  const [selectedPeriod, setSelectedPeriod] = useState("Month: Oct 2024");
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isLoaded, setIsLoaded] = useState(false);
  const [userId, setUserId] = useState(null);
  const [totalComplaints, setTotalComplaints] = useState(0);
  const [pendingComplaints, setPendingComplaints] = useState(0);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");

  // Confirmation Modal states
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [selectedAction, setSelectedAction] = useState(""); // "accept" or "reopen"

  // Bank and Branch filter states
  const [selectedBankId, setSelectedBankId] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [banksList, setBanksList] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(true);
  const [reopenRemarks, setReopenRemarks] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [reopenedTo, setReopenedTo] = useState("");
  const [userNames, setUserNames] = useState([]);
  const [priorities, setPriorities] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [currentUser, setCurrentUser] = useState(null);
  const statusOptions = [
    "Status - All",
    "Open",
    "In Progress",
    "Resolved",
    "Closed",
    // "Rejected",
  ];

  // Modal functions
  const openModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalTitle("");
    setModalContent("");
  };

  // Confirmation Modal functions
  const openConfirmationModal = (grievance, action) => {
    setSelectedGrievance(grievance);
    setSelectedAction(action);
    setShowConfirmationModal(true);
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
    setSelectedGrievance(null);
    setSelectedAction("");
    setIsAccepting(false);
    // Reset reopen specific states
    setReopenRemarks("");
    setSelectedFiles([]);
    setFileErrors([]);
    setReopenedTo("");
  };

  // Enhanced file handling for multiple files
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = [];
    const newErrors = [];

    files.forEach((file, index) => {
      // Check file size (1MB = 1048576 bytes)
      if (file.size > 1048576) {
        newErrors.push({
          fileName: file.name,
          error: `File size exceeds 1MB limit (${(file.size / 1048576).toFixed(2)}MB)`,
        });
        return;
      }

      // Check file type
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (!allowedTypes.includes(file.type)) {
        newErrors.push({
          fileName: file.name,
          error: "File type not supported",
        });
        return;
      }

      // Check if file already exists
      if (
        selectedFiles.some(
          (f) => f.file.name === file.name && f.file.size === file.size
        )
      ) {
        newErrors.push({
          fileName: file.name,
          error: "File already selected",
        });
        return;
      }

      // Add valid file
      newFiles.push({
        id: Date.now() + index, // Unique ID for each file
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadDate: new Date().toLocaleString(),
      });
    });

    // Update state with new files
    setSelectedFiles((prev) => [...prev, ...newFiles]);

    // Show errors if any
    if (newErrors.length > 0) {
      setFileErrors((prev) => [...prev, ...newErrors]);
      toast.error(
        `${newErrors.length} file(s) were rejected. Please check file requirements.`,
        {
          position: "top-center",
          autoClose: 3000,
        }
      );
    }

    // Clear the input
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "#e8f4f8";
    e.currentTarget.style.borderColor = "#2c7a95";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "#f8fbfc";
    e.currentTarget.style.borderColor = "#1a7a73";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "#f8fbfc";
    e.currentTarget.style.borderColor = "#1a7a73";

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.files = e.dataTransfer.files;
      input.onchange = (event) => handleFileChange(event);
      input.click();
    }
  };

  const removeFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setFileErrors([]);
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes("image")) return <Image size={16} />;
    if (fileType.includes("pdf")) return <FileText size={16} />;
    return <FileTextIcon size={16} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Status mapping - assuming status IDs map to these values
  const getStatusFromId = (statusId) => {
    const statusMap = {
      837: "Open",
      838: "In Progress",
      839: "Hold",
      840: "Resolved",
      841: "Closed",
      842: "Rejected",
      843: "In progress someone",
      844: "Resolved someone",
      845: "Escalated to L2",
      846: "Accept",
      847: "Accepted someone",
    };
    return statusMap[statusId] || "Open";
  };

  // Transform grievanceStatusMapEntities with status text
const transformGrievanceStatusEntities = (grievanceStatusMapEntities) => {
    if (!grievanceStatusMapEntities || !Array.isArray(grievanceStatusMapEntities)) {
        return [];
    }
    
    return grievanceStatusMapEntities.map(entity => ({
        ...entity, // Keep all original properties (id, createdBy, grievanceId, createdOn, status)
        statusText: getStatusFromId(entity.status) // Add readable status text
    }));
};

// Add this function after transformGrievanceStatusEntities
const processSortedListData = (sortedListData) => {
    return sortedListData.map(item => ({
        ...item,
        grievanceStatusMapEntities: transformGrievanceStatusEntities(item.grievanceStatusMapEntities)
    }));
};


  //dd-mm-yyyy date format
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = String(date.getFullYear()).toString();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, "0");

    const formattedTime = `${formattedHours}:${minutes} ${ampm}`;

    return `${dd}-${mm}-${yyyy} ${formattedTime}`;
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

   const downloadPDF = async () => {
  try {
    const doc = new jsPDF("l", "pt", "a4");
    let currentY = 50;

    let fontName = "Helvetica";
    doc.setFont(fontName);
    doc.setFontSize(13);

    const pdfTitle = "Grievance Report";

    const pageWidth = doc.internal.pageSize.width;
    const xOffset = pageWidth / 2;
    doc.text(pdfTitle, xOffset, currentY, { align: "center" });
    currentY += 30;

    // Define consistent column widths for all tables
    const columnStyles = {
      0: { cellWidth: 85 },   // Ticket ID
      1: { cellWidth: 95 },   // Bank Name
      2: { cellWidth: 75 },   // Branch Name
      3: { cellWidth: 85 },   // Incident Date
      4: { cellWidth: 90 },   // Department
      5: { cellWidth: 60 },   // Category
      6: { cellWidth: 60 },   // Escalation Level
      7: { cellWidth: 60 },   // Priority
      8: { cellWidth: 51 },   // Status
      9: { cellWidth: 80 }    // Status Updated
    };

    // Define consistent header padding
    const headerPaddings = {
      0: { top: 5, right: 5, bottom: 5, left: 25 },   // Ticket ID
      1: { top: 5, right: 30, bottom: 5, left: 75 },  // Bank Name  
      2: { top: 5, bottom: 5, left: 35 },             // Branch Name
      3: { top: 5, bottom: 5, left: 25 },             // Incident Date
      4: { top: 5, right: 15, bottom: 5,  left: 12 },            // Assigned Department
      5: { top: 5, right: 12, bottom: 5, left: 20 },  // Category
      6: { top: 5, right: 10, bottom: 5, left: 15 },  // Escalation Level
      7: { top: 5, right: 15, bottom: 5, left: 10 },  // Priority
      8: { top: 5, right: 8, bottom: 5, left: 5 },   // Status
      9: { top: 5, right: 12, bottom: 5, left: 15 }    // Status Updated
    };

    // Add single header row for the entire table
    const allHeaders = [
      "Ticket ID", "Bank Name", "Branch Name", "Issue Reported Date & Time", 
      "Assigned Department", "Category", "Escalation Level", "Priority", 
      "Status", "Status Updated Date & Time"
    ];

    doc.autoTable({
      head: [allHeaders],
      body: [], // Empty body for header only
      startY: currentY,
      theme: "grid",
      headStyles: {
        fillColor: [24, 163, 160],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        fontSize: 8,
      },
      didParseCell: function(data) {
        if (data.row.section === 'head') {
          if (headerPaddings[data.column.index]) {
            data.cell.styles.cellPadding = headerPaddings[data.column.index];
          }
        }
      },
      columnStyles: columnStyles,
      margin: { left: 50, right: 50 },
    });

    currentY = doc.lastAutoTable.finalY;

    // Process each grievance
    for (let i = 0; i < filteredComplaints.length; i++) {
      const grievance = filteredComplaints[i];
      
      // Check if we need a new page
      if (currentY > 700) {
        doc.addPage();
        currentY = 50;
        
        // Re-add header on new page
        doc.autoTable({
          head: [allHeaders],
          body: [],
          startY: currentY,
          theme: "grid",
          headStyles: {
            fillColor: [24, 163, 160],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            fontSize: 8,
          },
          didParseCell: function(data) {
            if (data.row.section === 'head') {
              if (headerPaddings[data.column.index]) {
                data.cell.styles.cellPadding = headerPaddings[data.column.index];
              }
            }
          },
          columnStyles: columnStyles,
          margin: { left: 50, right: 50 },
        });
        currentY = doc.lastAutoTable.finalY;
      }

      // Check if this grievance has previous tickets
      const oldGrievances = grievance.originalData?.oldGrievanceInformationEntity || [];
      const hasOldGrievances = oldGrievances.length > 0;

      // Main grievance data row with consistent column widths
      const mainData = [[
        grievance.id || "N/A",
        grievance.bank || "N/A",
        grievance.branch || "N/A", 
        grievance.date || "N/A",
        grievance.handler || "N/A",
        grievance.issueTypeName || "N/A",
        grievance.userLevels || "N/A",
        grievance.criticality || "N/A",
        grievance.status || "N/A",
        grievance.lastStatusUpdated || "N/A"
      ]];

      doc.autoTable({
        head: [], // No header for data rows
        body: mainData,
        startY: currentY,
        theme: "grid",
        bodyStyles: {
          fontSize: 7,
          halign: "left",
          fillColor: hasOldGrievances ? [220, 248, 198] : [255, 255, 255],
        },
        columnStyles: columnStyles, // Use same column widths
        margin: { left: 50, right: 50 },
      });

      currentY = doc.lastAutoTable.finalY;

      // Previous Tickets Section - only if tickets exist
      if (oldGrievances.length > 0) {
        // Check if we need a new page for previous tickets
        if (currentY > 650) {
          doc.addPage();
          currentY = 50;
        }

        // Previous tickets data
        const prevData = oldGrievances.map(oldGrievance => {
          // Get last status updated for old grievances
          const statusUpdates = oldGrievance.grievanceStatusMapEntities || [];
          let lastStatusUpdated = "N/A";
          
          if (statusUpdates.length > 0) {
            const sortedStatusUpdates = [...statusUpdates].sort((a, b) => 
              new Date(b.createdOn) - new Date(a.createdOn)
            );
            lastStatusUpdated = formatDateTime(sortedStatusUpdates[0].createdOn);
          }

          return [
            oldGrievance.grievanceSeqId || 'NA',
            oldGrievance.bankName || 'NA',
            oldGrievance.branchName || 'NA',
            formatDateTime(oldGrievance.ticketDateAndTime || oldGrievance.createdon) || 'NA',
            oldGrievance.assignedTeamName || 'NA',
            oldGrievance.issueTypeName || 'NA',
            oldGrievance.userLevel || 'NA',
            oldGrievance.priority || 'NA',
            getStatusFromId(oldGrievance.status) || 'Open',
            lastStatusUpdated || 'N/A'
          ];
        });

        // Add Previous Tickets section title
        currentY += 10;
        doc.setFontSize(10);
        doc.setFont(fontName, 'bold');
        doc.text(`Ticket Lineage (${oldGrievances.length} Record${oldGrievances.length !== 1 ? 's' : ''})`, 50, currentY);
        currentY += 10;

        // Use same headers and column styles for previous tickets
        doc.autoTable({
          head: [allHeaders], // Same headers
          body: prevData,
          startY: currentY,
          theme: 'grid',
          headStyles: {
            fillColor: [72, 153, 183], // Blue color for previous tickets header
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 7,
            cellPadding: { top: 5, right: 3, bottom: 5, left: 3 }
          },
          bodyStyles: {
            fontSize: 6,
            fillColor: [248, 249, 250], // Light background for previous tickets data
            halign: 'left',
            cellPadding: { top: 4, right: 3, bottom: 4, left: 3 }
          },
          columnStyles: columnStyles, // Use same column widths
          margin: { left: 50, right: 50 }
        });

        currentY = doc.lastAutoTable.finalY;

        // Add separator line only if current grievance has previous tickets AND it's not the last grievance
        if (i < filteredComplaints.length - 1) {
          currentY += 15;
          doc.setDrawColor(200, 200, 200);
          doc.line(50, currentY, pageWidth - 50, currentY);
          currentY += 15;
        }
      }
    }

    // Footer section
    const pageHeight = doc.internal.pageSize.height;
    const footerHeight = 180;
    const finalY = doc.lastAutoTable?.finalY || currentY;

    if (finalY + footerHeight > pageHeight) {
      doc.addPage();
      currentY = 50;
    }

    let footerY = pageHeight - 180;

    doc.setFontSize(10);
    doc.setFont(fontName, "normal");

    // Left side footer items
    doc.text("Letter No. :", 50, footerY + 100);
    doc.text("Date :", 50, footerY + 120);
    doc.text("Copy :", 50, footerY + 140);

    // Right side footer items
    doc.text("XXXX", 550, footerY + 100);
    doc.text("XXXXXXXXX", 550, footerY + 120);
    doc.text("XXXXXX", 550, footerY + 140);

    // Generate filename with timestamp
    const now = new Date();
    const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const seconds = now.getSeconds().toString().padStart(2, "0");
    const istTime = `${hours}${minutes}${seconds}`;
    const filename = `grievance_report_${istDate}_${istTime}.pdf`;

    doc.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    toast.error("Error generating PDF. Please try again.");
  }
};


  const downloadExcel = async () => {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Grievance Data");

    // Add title
    const titleRow = worksheet.addRow(["Grievance Report"]);
    titleRow.font = { bold: true, size: 16 };
    titleRow.alignment = { horizontal: "center" };
    worksheet.mergeCells("A1:J1"); // Now 10 columns total (A to J)

    // Add empty row
    worksheet.addRow([]);
    let currentRow = 3;

    // Updated header row with Category and new column
    const allHeaders = [
      "Ticket ID",
      "Bank Name",
      "Branch Name",
      "Issue Reported Date & Time",
      "Assigned Department",
      "Category", // Added Category back
      "Escalation Level",
      "Priority",
      "Status",
      "Status Updated Date & Time", // New column
    ];

    const headerRow = worksheet.addRow(allHeaders);
    headerRow.font = { bold: true };

    // Apply header styling ONLY to the actual header cells (A to J)
    for (let col = 1; col <= 10; col++) {
      const cell = headerRow.getCell(col);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF18A3A0" },
      };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    }

    currentRow = 4;

    // Process each grievance
    filteredComplaints.forEach((grievance, index) => {
      // Check if this grievance has previous tickets
      const oldGrievances =
        grievance.originalData?.oldGrievanceInformationEntity || [];
      const hasOldGrievances = oldGrievances.length > 0;

      // Main grievance data row
      const mainDataRow = worksheet.addRow([
        grievance.id || "N/A",
        grievance.bank || "N/A",
        grievance.branch || "N/A",
        grievance.date || "N/A",
        grievance.handler || "N/A",
        grievance.issueTypeName || "N/A", // Category data
        grievance.userLevels || "N/A",
        grievance.criticality || "N/A",
        grievance.status || "N/A",
        grievance.lastStatusUpdated || "N/A", // New column
      ]);

      // Add styling to main data row cells (only columns A to J)
      for (let col = 1; col <= 10; col++) {
        const cell = mainDataRow.getCell(col);

        // Add light green background if this grievance has previous tickets
        if (hasOldGrievances) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFDCF8C6" }, // Light green color
          };
        }

        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }

      currentRow++;

      // Previous Tickets Section - only if tickets exist
      if (oldGrievances.length > 0) {
        // Previous tickets section header
        const prevSectionRow = worksheet.addRow([
          `Ticket Lineage (${oldGrievances.length} Record${oldGrievances.length !== 1 ? "s" : ""})`,
        ]);
        prevSectionRow.font = { bold: true, size: 11 };

        // Apply styling ONLY to the merged range (A to J)
        for (let col = 1; col <= 10; col++) {
          const cell = prevSectionRow.getCell(col);
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF666666" },
          };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }

        worksheet.mergeCells(`A${currentRow}:J${currentRow}`); // Only merge A to J
        currentRow++;

        // Blue header row for previous tickets
        const prevHeaderRow = worksheet.addRow(allHeaders);
        prevHeaderRow.font = { bold: true };

        // Apply blue header styling ONLY to columns A to J
        for (let col = 1; col <= 10; col++) {
          const cell = prevHeaderRow.getCell(col);
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4899B7" }, // Blue color for previous tickets
          };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { horizontal: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }

        currentRow++;

        // Previous tickets data rows
        oldGrievances.forEach((oldGrievance) => {
          // Get last status updated for old grievances
          const statusUpdates = oldGrievance.grievanceStatusMapEntities || [];
          let lastStatusUpdated = "N/A";
          
          if (statusUpdates.length > 0) {
            const sortedStatusUpdates = [...statusUpdates].sort((a, b) => 
              new Date(b.createdOn) - new Date(a.createdOn)
            );
            lastStatusUpdated = formatDateTime(sortedStatusUpdates[0].createdOn);
          }

          const prevDataRow = worksheet.addRow([
            oldGrievance.grievanceSeqId || "N/A",
            oldGrievance.bankName || "N/A",
            oldGrievance.branchName || "N/A",
            formatDateTime(
              oldGrievance.ticketDateAndTime || oldGrievance.created_on
            ) || "N/A",
            oldGrievance.assignedTeamName || "N/A",
            oldGrievance.issueTypeName || "N/A", // Category for old grievances
            oldGrievance.userLevel || "N/A",
            oldGrievance.priority || "N/A",
            getStatusFromId(oldGrievance.status) || "Open",
            lastStatusUpdated || "N/A", // New column for old grievances
          ]);

          // Apply light background and borders ONLY to columns A to J
          for (let col = 1; col <= 10; col++) {
            const cell = prevDataRow.getCell(col);
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF8F9FA" },
            };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          }

          currentRow++;
        });

        // Add separator row only if current grievance has previous tickets AND it's not the last grievance
        if (index < filteredComplaints.length - 1) {
          const separatorRow = worksheet.addRow(["―".repeat(50)]);
          separatorRow.font = { color: { argb: "FF999999" } };

          // Apply styling and borders ONLY to the merged range (A to J)
          for (let col = 1; col <= 10; col++) {
            const cell = separatorRow.getCell(col);
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          }

          worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
          currentRow++;

          // Add empty row after separator
          const emptyRow = worksheet.addRow([]);
          // Add borders to empty row ONLY to columns A to J
          for (let col = 1; col <= 10; col++) {
            const cell = emptyRow.getCell(col);
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          }
          currentRow++;
        }
      }
    });

    // Set column widths ONLY for columns A to J
    worksheet.getColumn("A").width = 18; // Ticket ID
    worksheet.getColumn("B").width = 25; // Bank Name
    worksheet.getColumn("C").width = 18; // Branch Name
    worksheet.getColumn("D").width = 22; // Incident Date
    worksheet.getColumn("E").width = 25; // Department
    worksheet.getColumn("F").width = 20; // Category
    worksheet.getColumn("G").width = 15; // Escalation Level
    worksheet.getColumn("H").width = 12; // Priority
    worksheet.getColumn("I").width = 15; // Status
    worksheet.getColumn("J").width = 20; // Status Updated Date & Time

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    // Generate filename
    const now = new Date();
    const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const seconds = now.getSeconds().toString().padStart(2, "0");
    const istTime = `${hours}${minutes}${seconds}`;
    const filename = `grievance_report_${istDate}_${istTime}.xlsx`;

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error generating Excel:", error);
    toast.error("Error generating Excel file. Please try again.");
  }
};

  const fetchUserProfile = async () => {
    try {
      const response = await getData(getThemeURL);
      console.log("User Profile Response:", response);
      if (response && Array.isArray(response) && response.length > 0) {
        setUserId(response[0].id);
        setCurrentUser(response[0]); // Store the current user data
        // Add this line to set the user role
        setUserRole(response[0].role_name);
        setIsAdmin(response[0].is_admin);
        console.log("User Level from profile:", response[0].user_level);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };
  const getCurrentDateTime = () => {
    const now = new Date();
    return formatDateTimeForAPI(now);
  };

  //to fetch all grievance data
  useEffect(
    () => {
      if (userId) {
        getAllGrievanceData();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId]
  );

  const getAllGrievanceData = async () => {
    setLoader(true); // Start loading
    try {
      // Get user profile to determine role and construct payload
      const userProfile = await getData(getThemeURL);

      if (
        !userProfile ||
        !Array.isArray(userProfile) ||
        userProfile.length === 0
      ) {
        toast.error("Failed to fetch user profile", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        setLoader(false); // Stop loading on error
        return;
      }

      const user = userProfile[0];

      // Construct payload based on user role
      const payload = createGrievancePayload(user);

      // Fetch grievance data with role-based payload
      const grievanceData = await postDataWithBody(
        fetchGrievanceData,
        payload,
        {}
      );

      if (Array.isArray(grievanceData)) {
        // Sort by creation date (newest first)
        // const sortedList = grievanceData.sort((a, b) => {
        //   return new Date(b.created_on) - new Date(a.created_on);
        // });

        // ADD THIS LINE - Process the sortedList to transform grievanceStatusMapEntities
const processedSortedList = processSortedListData(grievanceData);

        // Transform the data to match table structure
        const transformedData = processedSortedList.map((item) => {
  // Get the latest status update from grievanceStatusMapEntities
  const statusUpdates = item.grievanceStatusMapEntities || [];
  let lastStatusUpdated = "N/A";
  
  if (statusUpdates.length > 0) {
    // Sort by createdOn date to get the latest status update
    const sortedStatusUpdates = [...statusUpdates].sort((a, b) => 
      new Date(b.createdOn) - new Date(a.createdOn)
    );
    lastStatusUpdated = formatDateTime(sortedStatusUpdates[0].createdOn);
  }

  return {
    id: item.grievanceSeqId,
    bank: item.bankName,
    branch: item.branchName,
    bankCode: item.bankCode,
    branchCode: item.branchCode,
    channel: "Web",
    type: item.issueTypeName,
    subject: item.subject,
    details: item.description
      ? item.description.replace(/<^>]*>/g, "")
      : "",
    date: formatDateTime(item.ticketDateAndTime || item.created_on),
    criticality: item.priority,
    status: getStatusFromId(item.status),
    handler: item.assignedTeamName,
    issueTypeName: item.issueTypeName,
    userLevels: item.userLevel,
    customerName: item.customerName,
    email: item.email,
    phoneNumber: item.phoneNumber,
    issueTypeSubCatName: item.issueTypeSubCatName,
    resolutionMethod: item.resoultionName,
    mediaList: item.mediaList || [],
    originalData: item,
    isDisable: item.is_disable,
    lastStatusUpdated: lastStatusUpdated, // Add this line
  };
});

        setComplaints(transformedData);
        setFilteredComplaints(transformedData);
        setRowCount(transformedData.length);
        setTotalPages(Math.ceil(transformedData.length / itemsPerPage));
        setTotalComplaints(transformedData.length);

        // pending complaints (Open)
        const pending = transformedData.filter(
          (item) => item.status === "Open"
        ).length;
        setPendingComplaints(pending);

        setIsLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching grievance data:", error);
      toast.error("Failed to load grievance data");
      setIsLoaded(true);
    } finally {
      setLoader(false); // Stop loading whether success or error
    }
  };

  // Helper function to create role-based payload for grievance data
  const createGrievancePayload = (userProfile) => {
    const {
      role_name,
      bank_id,
      id: userId,
      is_admin,
      user_level,
    } = userProfile;

    console.log(is_admin, isAdmin, "rolebased");

    // SUPER ADMIN role
    if (role_name === "SUPER ADMIN") {
      return {
        admin_role: "SA",
        bank_id: null,
        branch_id: null,
        userId: userId || null,
        userLevel: user_level || null,
      };
    }

    // HO role
    if (is_admin === true) {
      return {
        admin_role: "HO",
        bank_id: bank_id || null,
        branch_id: null,
        userId: userId || null,
        userLevel: user_level || null,
      };
    }

    // Default fallback (shouldn't happen in normal cases)
    return {
      admin_role: "USER",
      bank_id: null,
      branch_id: null,
      userId: userId || null,
      userLevel: user_level || null,
    };
  };

  //To load data in table based on the filtered items
  useEffect(() => {
    if (isLoaded) {
      setTotalPages(Math.ceil(filteredComplaints.length / itemsPerPage) || 1);
    }
  }, [itemsPerPage, filteredComplaints, isLoaded]);

  console.log("totalPages", totalPages);
  console.log("setItemsPerPage", setItemsPerPage);

  const applyFilters = () => {
    if (!isLoaded) return;
    let filtered = [...complaints];

    // Apply status filter
    if (statusFilter !== "Status - All") {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    // Apply bank filter
    if (selectedBankId && selectedBankId !== "") {
      filtered = filtered.filter(
        (item) =>
          item.bankId === parseInt(selectedBankId) ||
          item.bank === getBankNameById(selectedBankId)
      );
    }

    // Apply branch filter
    if (selectedBranch && selectedBranch !== "") {
      filtered = filtered.filter((item) => {
        // Only filter items that have valid branch data (not "NA")
        if (!item.branch || item.branch === "NA") {
          return false;
        }
        return (
          item.branchId === parseInt(selectedBranch) ||
          item.branch === getBranchNameById(selectedBranch)
        );
      });
    }

    // Enhanced search filter that includes old grievances
  if (searchQuery.trim() !== "") {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter((item) => {
      // Check main grievance data
      const mainMatches = 
        (item.id && item.id.toString().toLowerCase().includes(query)) ||
        (item.type && item.type.toLowerCase().includes(query)) ||
        (item.subject && item.subject.toLowerCase().includes(query)) ||
        (item.details && item.details.toLowerCase().includes(query)) ||
        (item.branch && item.branch.toLowerCase().includes(query)) ||
        (item.bank && item.bank.toLowerCase().includes(query)) ||
        (item.customerName && item.customerName.toLowerCase().includes(query));

      if (mainMatches) return true;

      // Check old grievances data
      const oldGrievances = item.originalData?.oldGrievanceInformationEntity || [];
      const oldGrievanceMatches = oldGrievances.some(oldGrievance => 
        (oldGrievance.grievanceSeqId && oldGrievance.grievanceSeqId.toString().toLowerCase().includes(query)) ||
        (oldGrievance.bankName && oldGrievance.bankName.toLowerCase().includes(query)) ||
        (oldGrievance.branchName && oldGrievance.branchName.toLowerCase().includes(query)) ||
        (oldGrievance.issueTypeName && oldGrievance.issueTypeName.toLowerCase().includes(query)) ||
        (oldGrievance.subject && oldGrievance.subject.toLowerCase().includes(query))
      );

      return oldGrievanceMatches;
    });
  }

    setFilteredComplaints(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage) || 1);
    setRowCount(filtered.length);

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Helper functions to get names by ID
  const getBankNameById = (bankId) => {
    const bank = banksList.find((bank) => bank.id === parseInt(bankId));
    return bank ? bank.name : "";
  };

  const getBranchNameById = (branchId) => {
    const branch = filteredBranches.find(
      (branch) => branch.id === parseInt(branchId)
    );
    return branch ? branch.name : "";
  };

  //Filters
  useEffect(
    () => {
      if (isLoaded) {
        applyFilters();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusFilter, searchQuery, selectedBankId, selectedBranch, selectedPeriod]
  );

  console.log("setSelectedPeriod", setSelectedPeriod);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchPriorities = async () => {
      try {
        const response = await getData(getAllSLA);
        if (response && Array.isArray(response)) {
          // Extract unique priorities from the response
          const priorityList = response;
          // .filter(item => item.activeStatus && !item.is_deleted)
          // .map(item => item.priority);
          setPriorities(priorityList);
        }
      } catch (error) {
        console.error("Error fetching priorities:", error);
      }
    };

    fetchPriorities();
  }, []);

  const formatDateTimeForAPI = (date) => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, "0");

    return `${dd}-${mm}-${yyyy} ${formattedHours}:${minutes} ${ampm}`;
  };

  const calculateSLADate = (priority, currentDate) => {
    if (!priority || !priorities || priorities.length === 0) {
      // Default fallback: 2 hours if no SLA data found
      return new Date(currentDate.getTime() + 2 * 60 * 60 * 1000);
    }

    // Find the SLA configuration for the given priority
    const slaConfig = priorities.find(
      (sla) =>
        sla.priority && sla.priority.toLowerCase() === priority.toLowerCase()
    );

    if (!slaConfig) {
      // Default fallback: 2 hours if priority not found in SLA data
      return new Date(currentDate.getTime() + 2 * 60 * 60 * 1000);
    }

    // Calculate SLA date based on maxResTimeValue and maxResTimeType
    const { maxResTimeValue, maxResTimeType } = slaConfig;

    let timeToAdd = 0;

    switch (maxResTimeType?.toLowerCase()) {
      case "minutes":
        timeToAdd = maxResTimeValue * 60 * 1000; // Convert minutes to milliseconds
        break;
      case "hours":
        timeToAdd = maxResTimeValue * 60 * 60 * 1000; // Convert hours to milliseconds
        break;
      case "days":
        timeToAdd = maxResTimeValue * 24 * 60 * 60 * 1000; // Convert days to milliseconds
        break;
      default:
        // Default to hours if type is not recognized
        timeToAdd = maxResTimeValue * 60 * 60 * 1000;
    }

    return new Date(currentDate.getTime() + timeToAdd);
  };

  // Handle Action Confirmation
  const handleActionConfirmation = async () => {
    if (!selectedGrievance || !selectedAction) return;

    try {
      setIsAccepting(true);
      setLoader(true); // Show loader during action processing

      if (selectedAction === "accept") {
        // Get user profile to determine user level
        const userProfile = await getData(getThemeURL);
        const currentUser = userProfile && userProfile[0];
        const userLevel = currentUser?.user_level;

        // Create FormData payload for acceptance
        const formData = new FormData();

        // Base grievance data
        const grievanceData = {
          id: selectedGrievance.originalData.id,
          updated_by: userId,
        };

        // Add level-specific fields based on user level
        if (userLevel === "L3") {
          grievanceData.isAcceptToL3 = true;
          grievanceData.acceptedByL3 = userId;
        } else if (userLevel === "L2") {
          grievanceData.isAcceptToL2 = true;
          grievanceData.acceptedByL2 = userId;
        } else {
          grievanceData.isAccept = true;
          grievanceData.acceptedBy = userId;
        }

        formData.append("grievance", JSON.stringify(grievanceData));

        // Call the acceptAfterEsclation API
        // eslint-disable-next-line no-unused-vars
        const response = await postDataWithBody(
          acceptAfterEsclation,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast.success(
          `Grievance ${selectedGrievance.id} accepted successfully`,
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
      } else if (selectedAction === "reopen") {
        // Handle reopen action with FormData
        const formData = new FormData();

        // Get current date and time in the required format (dd-mm-yyyy hh:mm AM/PM)
        const now = new Date();
        const reopenedDate = formatDateTimeForAPI(now);

        // Calculate resolution SLA date based on priority
        const priority =
          selectedGrievance.criticality ||
          selectedGrievance.originalData.priority;
        const resSLADate = calculateSLADate(priority, now);
        const resSLADateString = formatDateTimeForAPI(resSLADate);

        const reopenedToValue = reopenedTo
          ? parseInt(reopenedTo)
          : selectedGrievance.originalData.createdBy;

        // Construct the grievance object as per API requirements
        const grievanceData = {
          id: selectedGrievance.originalData.id,
          reOpenedDateAndTimeString: reopenedDate,
          resSLADateAndTimeString: resSLADateString,
          reOpenedTo: reopenedToValue,
          reOpenedRemarks: reopenRemarks,
          userLevel: "L1",
        };

        // Append the grievance data as JSON string
        formData.append("grievance", JSON.stringify(grievanceData));

        if (selectedFiles.length > 0) {
          selectedFiles.forEach((fileObj, index) => {
            formData.append("files", fileObj.file);
          });
        } else {
          // Append null or empty file if no files are selected
          // Some APIs might require at least one file field, so we can append an empty file
          formData.append("files", new Blob(), "empty.txt");
          // Or simply don't append anything if the API accepts no files
        }
        // Call the reopenAfterClosed API
        // eslint-disable-next-line no-unused-vars
        const response = await postDataWithBody(reopenAfterClosed, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success(
          `Grievance ${selectedGrievance.id} reopened successfully with ${selectedFiles.length} file(s)`,
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
      }

      // Close confirmation modal and reset states
      closeConfirmationModal();
      setReopenRemarks("");
      setSelectedFiles([]);
      setFileErrors([]);
      setReopenedTo("");

      // Refresh the grievance data to reflect the changes
      getAllGrievanceData();
    } catch (error) {
      console.error(`Error ${selectedAction}ing grievance:`, error);
      toast.error(`Failed to ${selectedAction} grievance. Please try again.`, {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsAccepting(false);
      setLoader(false); // Hide loader after action completes
    }
  };

  //color and background for statuses
  const renderStatusBadge = (status) => {
    if (!status) return "NA";

    let backgroundColor = "";
    let dotColor = "";
    let textColor = "";

    switch (status) {
      case "Open":
        backgroundColor = "#FDE2E4";
        dotColor = "#D90429";
        textColor = "#D90429";
        break;
      case "In Progress":
      case "In progress someone":
        backgroundColor = "#FFF1E6";
        dotColor = "#FF7F11";
        textColor = "#FF7F11";
        break;
      case "Hold":
        backgroundColor = "#FFF8E1";
        dotColor = "#FFC107";
        textColor = "#FFC107";
        break;
      case "Resolved":
      case "Resolved someone":
        backgroundColor = "#E2F0CB";
        dotColor = "#228B22";
        textColor = "#228B22";
        break;
      case "Closed":
        backgroundColor = "#E0E8F9";
        dotColor = "#304FFE";
        textColor = "#304FFE";
        break;
      case "Rejected":
        backgroundColor = "#FBE3E8";
        dotColor = "#B00020";
        textColor = "#B00020";
        break;
      case "Escalated to L2":
        backgroundColor = "#E3F2FD";
        dotColor = "#4CA2C4";
        textColor = "#4CA2C4";
        break;
      case "Accept":
        backgroundColor = "#E8F5E9";
        dotColor = "#2E7D32";
        textColor = "#2E7D32";
        break;
      default:
        return "NA";
    }

    return (
      <div className="status-badge-container" style={{ position: "relative" }}>
        <div
          className="status-badge"
          style={{
            backgroundColor,
            color: textColor,
            borderRadius: "5px",
            padding: "4px 10px 4px 20px",
            display: "inline-block",
            width: "120px",
            height: "28px",
            lineHeight: "20px",
            fontFamily: "Poppins",
            fontWeight: 500,
            fontSize: "12px",
            position: "relative",
            textAlign: "left",
          }}
        >
          <div
            className="status-dot"
            style={{
              backgroundColor: dotColor,
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              position: "absolute",
              left: "8px",
              top: "10px",
            }}
          ></div>
          {status}
        </div>
      </div>
    );
  };

  //color and background for criticality
  const renderCriticality = (criticality) => {
    if (!criticality) return "NA";

    let textColor = "";

    switch (criticality.toUpperCase()) {
      case "CRITICAL":
        textColor = "#DC3545";
        break;
      case "HIGH":
        textColor = "#FD7E14";
        break;
      case "LOW":
        textColor = "#28A745";
        break;
      case "NORMAL":
        textColor = "#FFC107";
        break;
      default:
        textColor = "#808080";
    }

    return (
      <span
        style={{
          color: textColor,
          fontWeight: "bold",
          fontFamily: "Poppins",
          fontSize: "12px",
        }}
      >
        {criticality}
      </span>
    );
  };

  // Actions Icons Component with Lucide Icons
  const ActionsIcons = ({ grievance, userLevel }) => {
    // Check if actions should be shown (same logic as before)
    const shouldShowAccept =
      grievance.originalData.escalated &&
      userLevel &&
      grievance.originalData?.userLevel &&
      userLevel === grievance.originalData.userLevel;

    const shouldShowReopen =
      userLevel &&
      grievance.originalData?.userLevel &&
      userLevel === grievance.originalData.userLevel &&
      grievance.status === "Closed";

    if (!shouldShowAccept && !shouldShowReopen) {
      return null;
    }

    return (
      <div
        className="actions-icons-container"
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Accept Icon */}
        {shouldShowAccept && (
          <div
            className="action-icon accept-icon"
            onClick={() => openConfirmationModal(grievance, "accept")}
            style={{
              cursor: "pointer",
              padding: "6px",
              borderRadius: "4px",
              backgroundColor: "#E8F5E8",
              border: "1px solid #28A745",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#28A745";
              e.currentTarget.querySelector("svg").style.stroke = "white";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#E8F5E8";
              e.currentTarget.querySelector("svg").style.stroke = "#28A745";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="Accept Grievance"
          >
            <CircleCheckBig size={16} color="#28A745" />
          </div>
        )}

        {/* Reopen Icon */}
        {shouldShowReopen && (
          <div
            className="action-icon reopen-icon"
            onClick={() => openConfirmationModal(grievance, "reopen")}
            style={{
              cursor: "pointer",
              padding: "6px",
              borderRadius: "4px",
              backgroundColor: "#E3F2FD",
              border: "1px solid #2196F3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#2196F3";
              e.currentTarget.querySelector("svg").style.stroke = "white";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#E3F2FD";
              e.currentTarget.querySelector("svg").style.stroke = "#2196F3";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="Reopen Grievance"
          >
            <RefreshCcw size={16} color="#2196F3" />
          </div>
        )}
      </div>
    );
  };

  //table data
  const columns = React.useMemo(
    () => [
      {
        Header: t("Ticket ID"),
        accessor: "id",
        disableSortBy: true,
        Cell: ({ value }) => {
          return (
            <span
              style={{
                fontFamily: "Poppins",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {value || "NA"}
            </span>
          );
        },
      },
      {
        Header: t("Bank Name"),
        accessor: "bank",
        disableSortBy: true,
        Cell: ({ value }) => (
          <span
            style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
          >
            {value || "NA"}
          </span>
        ),
      },
      {
        Header: t("Branch Name"),
        accessor: "branch",
        disableSortBy: true,
        Cell: ({ value }) => (
          <span
            style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
          >
            {value || "NA"}
          </span>
        ),
      },
      {
        Header: t("Issue Reported Date & Time"),
        accessor: "date",
        disableSortBy: true,
        Cell: ({ value }) => (
          <span
            style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
          >
            {value || "NA"}
          </span>
        ),
      },
      {
        Header: t("Assigned Department"),
        accessor: "handler",
        width: 100,
        disableSortBy: true,
        Cell: ({ value }) => (
          <span
            onClick={() =>
              value && value !== "NA" && openModal("Assigned Department", value)
            }
            style={{
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: 500,
              maxWidth: "80px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              cursor: value && value !== "NA" ? "pointer" : "default",
            }}
            title="Click to view full assigned team"
          >
            {value || "NA"}
          </span>
        ),
      },
      // {
      //   Header: t("Category"),
      //   accessor: "issueTypeName",
      //   disableSortBy: true,
      //   Cell: ({ value }) => (
      //     <span
      //       style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
      //     >
      //       {value || "NA"}
      //     </span>
      //   ),
      // },
      {
        Header: t("Escalation Level"),
        accessor: "userLevels",
        width: 80,
        disableSortBy: true,
        Cell: ({ value }) => (
          <span
            style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
          >
            {value || "NA"}
          </span>
        ),
      },
      {
        Header: t("Priority"),
        accessor: "criticality",
        disableSortBy: true,
        Cell: ({ value }) => renderCriticality(value),
      },
      {
        Header: t("Status"),
        accessor: "status",
        disableSortBy: false,
        Cell: ({ value }) => renderStatusBadge(value || "Open"),
      },
      {
  Header: "Status Updated Date & Time",
  accessor: "lastStatusUpdated",
  disableSortBy: true,
  Cell: ({ value }) => (
    <span
      style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
    >
      {value || "N/A"}
    </span>
  ),
},

      {
        Header: t("Action"),
        disableSortBy: true,
        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const [userLevel, setUserLevel] = useState(null);
          // eslint-disable-next-line no-unused-vars
          const deletePopUp = () => {
            setIsdelete(!isdelete);
          };

          // Fetch user level from themeURL
          useEffect(() => {
            const fetchUserLevel = async () => {
              try {
                const response = await getData(getThemeURL);
                if (
                  response &&
                  Array.isArray(response) &&
                  response.length > 0
                ) {
                  setUserLevel(response[0].user_level);
                }
              } catch (error) {
                console.error("Error fetching user level:", error);
              }
            };
            fetchUserLevel();
          }, []);

          return (
            <div
              className="usertable__actionsicon"
              style={{ display: "flex", gap: "8px", alignItems: "center" }}
            >
              {/* View Icon */}
              <span
                onClick={() =>
                  navigate("/grievance/view-raise-ticket", {
                    state: { data: props.row.original },
                    device_type: true,
                  })
                }
                style={{ cursor: "pointer" }}
                title='View Grievance details'
              >
                <Viewicon value={props.value} />
              </span>

              {/* Actions Dropdown - Only show if conditions are met */}
              <ActionsIcons
                grievance={props.row.original}
                userLevel={userLevel}
              />
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  useEffect(
    () => {
      getUserProfile();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    // Fetch user names for L1 level by default, or adjust based on your needs
    if (userId) {
      fetchUserNames(); // You can change this to dynamic user level if needed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      setUserProfile(userProfile);
      if (userProfile && userProfile.length > 0) {
        fetchBanks(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const createPayload = (profile) => {
    let admin_role = "U"; // default value

    if (profile.is_admin === true) {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  const fetchUserNames = async () => {
    try {
      const response = await getData(
        getUsersByUserLevelExceptCurrent.replace("USERID", userId)
      );
      // if (Array.isArray(response)) {
      setUserNames(response);
      // } else {
      // setUserNames([]);
      // }
    } catch (error) {
      console.error("Error fetching cross department users:", error);
      setUserNames([]);
      toast.error("Failed to load users", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  //fetch banks and branches for filters
  const fetchBanks = async (profile) => {
    try {
      const payload = createPayload(profile);
      const response = await postDataWithBody(getallBanksByRole, payload, {});
      if (response && Array.isArray(response)) {
        setBanksList(response);
      } else {
        console.error("Invalid banks data format:", response);
        toast.error("Failed to load banks data");
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
      toast.error("Failed to load banks data");
    }
  };

  const fetchBranches = async (bankId) => {
    try {
      const response = await getData(getbarches.replace("ID", bankId));
      if (response && Array.isArray(response)) {
        setFilteredBranches(response);
      } else {
        console.error(
          `Invalid branches data format for bank ${bankId}:`,
          response
        );
        setFilteredBranches([]);
      }
    } catch (error) {
      console.error(`Error fetching branches for bank ${bankId}:`, error);
      setFilteredBranches([]);
    }
  };

  const handleBankChange = (e) => {
    const bankId = e.target.value;
    setSelectedBankId(bankId);
    setSelectedBranch(""); // Reset branch selection when bank changes

    if (bankId) {
      fetchBranches(bankId);
    } else {
      setFilteredBranches([]);
    }
  };

  useEffect(
    () => {
      if (
        isAdmin === true &&
        userProfile &&
        userProfile.length > 0 &&
        userProfile[0].bank_id
      ) {
        const bankId = userProfile[0].bank_id.toString();
        setSelectedBankId(bankId);
        fetchBranches(bankId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userRole, userProfile]
  );

  //based on the bank selection fetching branch details
  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
  };

  const formElementStyle = {
    height: "33px",
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "20px",
  };

  const statCardStyle = {
    backgroundColor: "#1a7a73",
    color: "white",
    width: "161px",
    height: "32px",
    borderRadius: "4px",
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px",
  };

  console.log(selectedGrievance, "selectedGrievanceselectedGrievance");

  return (
    <div className="complaint-dashboard">
      {/* Add Loader component here - similar to BankTable */}
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      {/* Modal */}
      {showModal && (
        <div
          className="modal"
          style={{
            display: "block",
            position: "fixed",
            zIndex: 1050,
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: "rgba(0,0,0,0.4)",
          }}
          onClick={closeModal}
        >
          <div
            className="modal-dialog modal-lg"
            style={{
              position: "relative",
              width: "auto",
              margin: "1.75rem auto",
              maxWidth: "800px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content"
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                backgroundColor: "#fff",
                backgroundClip: "padding-box",
                border: "1px solid rgba(0,0,0,.2)",
                borderRadius: "0.3rem",
                boxShadow: "0 0.25rem 0.5rem rgba(0,0,0,.5)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  borderTopLeftRadius: "0.3rem",
                  borderTopRightRadius: "0.3rem",
                }}
              >
                <h5
                  className="modal-title"
                  style={{
                    margin: 0,
                    lineHeight: 1.5,
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  {modalTitle}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  style={{
                    padding: 0,
                    background: "none",
                    border: 0,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    color: "#000",
                    textShadow: "0 1px 0 #fff",
                    opacity: 0.5,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
              <div
                className="modal-body"
                style={{
                  position: "relative",
                  flex: "1 1 auto",
                  padding: "1rem",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  wordWrap: "break-word",
                  maxHeight: "400px",
                  overflowY: "auto",
                }}
              >
                {modalContent}
              </div>
              <div
                className="modal-footer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  padding: "0.75rem",
                  borderTop: "1px solid #dee2e6",
                  borderBottomRightRadius: "0.3rem",
                  borderBottomLeftRadius: "0.3rem",
                }}
              >
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  style={{
                    backgroundColor: "#6c757d",
                    borderColor: "#6c757d",
                    color: "#fff",
                    padding: "0.375rem 0.75rem",
                    fontSize: "0.875rem",
                    lineHeight: 1.5,
                    borderRadius: "0.25rem",
                    border: "1px solid transparent",
                    cursor: "pointer",
                    fontFamily: "Poppins",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Confirmation Modal with Multiple File Upload */}
      {showConfirmationModal && (
        <div
          className="modal"
          style={{
            display: "block",
            position: "fixed",
            zIndex: 1060,
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: "rgba(0,0,0,0.4)",
          }}
          onClick={closeConfirmationModal}
        >
          <div
            className="modal-dialog"
            style={{
              position: "relative",
              width: "auto",
              margin: "1.75rem auto",
              maxWidth: selectedAction === "reopen" ? "600px" : "500px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content"
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                backgroundColor: "#fff",
                backgroundClip: "padding-box",
                border: "1px solid rgba(0,0,0,.2)",
                borderRadius: "0.3rem",
                boxShadow: "0 0.25rem 0.5rem rgba(0,0,0,.5)",
              }}
            >
              {/* Simple Confirmation for Accept Action */}
              {selectedAction === "accept" && (
                <>
                  <div
                    className="modal-header"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "1rem 1rem",
                      borderBottom: "1px solid #dee2e6",
                      borderTopLeftRadius: "0.3rem",
                      borderTopRightRadius: "0.3rem",
                    }}
                  >
                    <h5
                      className="modal-title"
                      style={{
                        margin: 0,
                        lineHeight: 1.5,
                        fontFamily: "Poppins",
                        fontWeight: 600,
                        fontSize: "16px",
                      }}
                    >
                      Confirm Acceptance
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeConfirmationModal}
                      style={{
                        padding: 0,
                        background: "none",
                        border: 0,
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        color: "#000",
                        textShadow: "0 1px 0 #fff",
                        opacity: 0.5,
                        cursor: "pointer",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div
                    className="modal-body"
                    style={{
                      position: "relative",
                      flex: "1 1 auto",
                      padding: "1rem",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      lineHeight: "1.6",
                      wordWrap: "break-word",
                    }}
                  >
                    <p style={{ margin: 0 }}>
                      Do you want to take the ownership of grievance{" "}
                      {selectedGrievance?.id}?
                    </p>
                  </div>
                  <div
                    className="modal-footer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      padding: "0.75rem",
                      borderTop: "1px solid #dee2e6",
                      borderBottomRightRadius: "0.3rem",
                      borderBottomLeftRadius: "0.3rem",
                      gap: "10px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={closeConfirmationModal}
                      disabled={isAccepting}
                      style={{
                        backgroundColor: "#6c757d",
                        borderColor: "#6c757d",
                        color: "#fff",
                        fontSize: "0.875rem",
                        lineHeight: 1.5,
                        borderRadius: "0.25rem",
                        border: "1px solid transparent",
                        cursor: isAccepting ? "not-allowed" : "pointer",
                        fontFamily: "Poppins",
                        opacity: isAccepting ? 0.6 : 1,
                        padding: "0.5rem 1rem",
                        width: "110px",
                        height: "38px",
                        textAlign: "center",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={handleActionConfirmation}
                      disabled={isAccepting}
                      style={{
                        backgroundColor: "#1a7a73",
                        borderColor: "#1a7a73",
                        color: "#fff",
                        fontSize: "0.875rem",
                        lineHeight: 1.5,
                        borderRadius: "0.25rem",
                        border: "1px solid transparent",
                        cursor: isAccepting ? "not-allowed" : "pointer",
                        fontFamily: "Poppins",
                        opacity: isAccepting ? 0.6 : 1,
                        padding: "0.5rem 1rem",
                        width: "110px",
                        height: "38px",
                        textAlign: "center",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isAccepting ? "Accepting..." : "Confirm"}
                    </button>
                  </div>
                </>
              )}

              {/* Enhanced Reopen Modal with Multiple File Upload */}
              {selectedAction === "reopen" && (
                <>
                  <div
                    className="modal-header"
                    style={{
                      borderBottom: "1px solid #e5e5e5",
                      padding: "12px 20px",
                      backgroundColor: "#f9fafb",
                      borderTopLeftRadius: "0.3rem",
                      borderTopRightRadius: "0.3rem",
                    }}
                  >
                    <h5
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#222",
                        margin: 0,
                        fontFamily: "Poppins",
                      }}
                    >
                      Reopen – {selectedGrievance?.id}
                    </h5>
                    <button
                      onClick={closeConfirmationModal}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "20px",
                        color: "#555",
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {/* Body */}
                  <div
                    className="modal-body"
                    style={{
                      padding: "20px",
                      fontSize: "14px",
                      color: "#333",
                      fontFamily: "Poppins",
                      maxHeight: "70vh",
                      overflowY: "auto",
                    }}
                  >
                    {[
                      ["Ticket ID", selectedGrievance?.id],
                      ["Bank Name", selectedGrievance?.bank || "N/A"],
                      ["Branch Name", selectedGrievance?.branch || "NA"],
                      ["Category", selectedGrievance?.type || "Software"],
                    ].map(([label, value]) => (
                      <div key={label} style={{ marginBottom: "10px" }}>
                        <label
                          style={{
                            display: "block",
                            fontWeight: "600",
                            marginBottom: "3px",
                            color: "#111",
                            fontSize: "13px",
                          }}
                        >
                          {label}
                        </label>
                        <input
                          type="text"
                          value={value}
                          readOnly
                          style={{
                            width: "100%",
                            border: "1px solid #dee2e6",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            backgroundColor: "#f8f9fa",
                            fontSize: "13px",
                            color: "#333",
                            fontWeight: "500",
                            fontFamily: "Poppins",
                          }}
                        />
                      </div>
                    ))}

                    <hr style={{ margin: "16px 0" }} />

                    {/* Original Closure Date */}
                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#333",
                        }}
                      >
                        Closure Date & Time
                      </label>
                      <input
                        type="text"
                        value={
                          selectedGrievance?.originalData.updated_on
                            ? formatDateTime(
                                selectedGrievance.originalData.updated_on
                              )
                            : "N/A"
                        }
                        readOnly
                        style={{
                          width: "100%",
                          border: "1px solid #dee2e6",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          backgroundColor: "#f8f9fa",
                          fontSize: "13px",
                          fontFamily: "Poppins",
                        }}
                      />
                    </div>

                    {/* Enhanced Multiple File Upload Section */}
                    <div style={{ marginBottom: "14px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <label
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#333",
                          }}
                        >
                          Attach Evidence / Screenshots
                        </label>
                        {selectedFiles.length > 0 && (
                          <button
                            onClick={clearAllFiles}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#ff4444",
                              fontSize: "11px",
                              cursor: "pointer",
                              textDecoration: "underline",
                              fontFamily: "Poppins",
                            }}
                          >
                            Clear All
                          </button>
                        )}
                      </div>

                      {/* Upload Area */}
                      <div
                        style={{
                          border: "2px dashed #1a7a73",
                          borderRadius: "8px",
                          padding: "20px",
                          textAlign: "center",
                          backgroundColor: "#f8fbfc",
                          cursor: "pointer",
                          position: "relative",
                          transition: "all 0.3s ease",
                          marginBottom: "12px",
                        }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <input
                          type="file"
                          multiple
                          accept="image/*,.png,.jpg,.jpeg,.pdf,.doc,.docx"
                          onChange={handleFileChange}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            cursor: "pointer",
                          }}
                        />
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#666",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{ fontSize: "24px", marginBottom: "8px" }}
                          >
                            📁
                          </div>
                          <div
                            style={{
                              fontWeight: "600",
                              color: "#1a7a73",
                              marginBottom: "4px",
                            }}
                          >
                            Click to upload or drag and drop
                          </div>
                          <div style={{ fontSize: "12px", color: "#888" }}>
                            Supports: PNG, JPG, JPEG, PDF (Max: 1MB per file)
                          </div>
                        </div>
                      </div>

                      {/* File Preview Section */}
                      {selectedFiles.length > 0 && (
                        <div
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "6px",
                            padding: "12px",
                            backgroundColor: "#f9f9f9",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#333",
                              }}
                            >
                              Selected Files ({selectedFiles.length})
                            </span>
                            <div style={{ fontSize: "11px", color: "#666" }}>
                              Total:{" "}
                              {formatFileSize(
                                selectedFiles.reduce(
                                  (acc, file) => acc + file.size,
                                  0
                                )
                              )}
                            </div>
                          </div>

                          <div
                            style={{ maxHeight: "200px", overflowY: "auto" }}
                          >
                            {selectedFiles.map((fileObj) => (
                              <div
                                key={fileObj.id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  padding: "8px",
                                  backgroundColor: "white",
                                  borderRadius: "4px",
                                  border: "1px solid #e8e8e8",
                                  marginBottom: "6px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#1a7a73",
                                    borderRadius: "4px",
                                    color: "white",
                                    flexShrink: 0,
                                  }}
                                >
                                  {getFileIcon(fileObj.type)}
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontWeight: "500",
                                      fontSize: "12px",
                                      color: "#333",
                                      marginBottom: "2px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                    title={fileObj.name}
                                  >
                                    {fileObj.name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "10px",
                                      color: "#666",
                                    }}
                                  >
                                    {formatFileSize(fileObj.size)} •{" "}
                                    {fileObj.type
                                      .split("/")[1]
                                      ?.toUpperCase() || "FILE"}
                                  </div>
                                </div>

                                <button
                                  onClick={() => removeFile(fileObj.id)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#ff4444",
                                    cursor: "pointer",
                                    padding: "4px",
                                    borderRadius: "3px",
                                    flexShrink: 0,
                                  }}
                                  title="Remove file"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Error Messages */}
                      {fileErrors.length > 0 && (
                        <div
                          style={{
                            border: "1px solid #ffcccc",
                            borderRadius: "6px",
                            padding: "10px",
                            backgroundColor: "#fff5f5",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#d63031",
                              marginBottom: "6px",
                            }}
                          >
                            Rejected Files ({fileErrors.length})
                          </div>
                          {fileErrors.map((error, index) => (
                            <div
                              key={index}
                              style={{
                                fontSize: "11px",
                                color: "#d63031",
                                marginBottom: "2px",
                              }}
                            >
                              • {error.fileName}: {error.error}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* No File Selected State */}
                      {selectedFiles.length === 0 && (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            color: "#999",
                            fontSize: "12px",
                            fontStyle: "italic",
                          }}
                        >
                          No files selected
                        </div>
                      )}
                    </div>

                    {/* Approved By */}
                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "600",
                          marginBottom: "3px",
                          color: "#111",
                          fontSize: "13px",
                        }}
                      >
                        Assigned Department
                      </label>
                      <input
                        type="text"
                        value={
                          selectedGrievance?.originalData.assignedTeamName
                            ? selectedGrievance.originalData.assignedTeamName
                            : "N/A"
                        }
                        readOnly
                        style={{
                          width: "100%",
                          border: "1px solid #dee2e6",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          backgroundColor: "#f8f9fa",
                          fontSize: "13px",
                          fontFamily: "Poppins",
                        }}
                      />
                    </div>

                    {/* Remarks */}
                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "600",
                          marginBottom: "3px",
                          color: "#111",
                          fontSize: "13px",
                        }}
                      >
                        Reason for Reopening
                        <span style={{ color: "red" }}> *</span>
                      </label>

                      <textarea
                        value={reopenRemarks}
                        onChange={(e) => setReopenRemarks(e.target.value)}
                        placeholder="Enter detailed reason for reopening this ticket..."
                        style={{
                          width: "100%",
                          border: "1px solid #ced4da",
                          borderRadius: "6px",
                          padding: "8px 10px",
                          fontSize: "13px",
                          fontWeight: "500",
                          resize: "vertical",
                          minHeight: "80px",
                          fontFamily: "Poppins",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          marginTop: "4px",
                        }}
                      >
                        Please provide specific details about why this ticket
                        needs to be reopened
                      </div>
                    </div>

                    {/* Reopened To */}
                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "600",
                          marginBottom: "3px",
                          color: "#111",
                          fontSize: "13px",
                        }}
                      >
                        L1 Userlist
                      </label>
                      <select
                        value={reopenedTo}
                        onChange={(e) => setReopenedTo(e.target.value)}
                        style={{
                          width: "100%",
                          border: "1px solid #ced4da",
                          borderRadius: "6px",
                          padding: "8px 10px",
                          fontSize: "13px",
                          fontWeight: "500",
                          fontFamily: "Poppins",
                        }}
                      >
                        <option value="">Select User</option>
                        {userNames.map((user) => (
                          <option
                            key={user.id || user.value}
                            value={user.id || user.value}
                          >
                            {user.first_name && user.last_name
                              ? `${user.first_name} ${user.last_name}`
                              : user.first_name
                                ? user.first_name
                                : user.user_name || user.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "600",
                          marginBottom: "3px",
                          color: "#111",
                          fontSize: "13px",
                        }}
                      >
                        Requested By
                      </label>
                      <input
                        type="text"
                        value={
                          userProfile &&
                          userProfile[0]?.first_name &&
                          userProfile[0]?.last_name
                            ? `${userProfile[0].first_name} ${userProfile[0].last_name}`
                            : userProfile?.[0]?.first_name ||
                              userProfile?.[0]?.user_name ||
                              "N/A"
                        }
                        readOnly
                        style={{
                          width: "100%",
                          border: "1px solid #dee2e6",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          backgroundColor: "#f8f9fa",
                          fontSize: "13px",
                          fontFamily: "Poppins",
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "600",
                          marginBottom: "3px",
                          color: "#111",
                          fontSize: "13px",
                        }}
                      >
                        Date and Time of reopen request
                      </label>
                      <input
                        type="text"
                        value={getCurrentDateTime()}
                        readOnly
                        style={{
                          width: "100%",
                          border: "1px solid #dee2e6",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          backgroundColor: "#f8f9fa",
                          fontSize: "13px",
                          fontFamily: "Poppins",
                        }}
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div
                    className="modal-footer"
                    style={{
                      borderTop: "1px solid #e5e5e5",
                      backgroundColor: "#f9fafb",
                      padding: "12px 20px",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "8px",
                      borderBottomRightRadius: "0.3rem",
                      borderBottomLeftRadius: "0.3rem",
                    }}
                  >
                    <button
                      onClick={closeConfirmationModal}
                      style={{
                        backgroundColor: "#6c757d",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 16px",
                        fontSize: "13px",
                        cursor: "pointer",
                        fontFamily: "Poppins",
                        fontWeight: "500",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleActionConfirmation}
                      disabled={isAccepting || !reopenRemarks.trim()}
                      style={{
                        backgroundColor: !reopenRemarks.trim()
                          ? "#ccc"
                          : "#1a7a73",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 16px",
                        fontSize: "13px",
                        cursor:
                          !reopenRemarks.trim() || isAccepting
                            ? "not-allowed"
                            : "pointer",
                        fontFamily: "Poppins",
                        fontWeight: "500",
                        opacity: isAccepting ? 0.6 : 1,
                      }}
                    >
                      {isAccepting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className="filter-row d-flex align-items-center mb-3"
        style={{ gap: "12px", paddingLeft: "30px", marginTop: "10px" }}
      >
        {/* Total Complaints stat card */}
        <div className="stat-card" style={statCardStyle}>
          Total Complaints: {totalComplaints}
        </div>

        <div className="stat-card" style={statCardStyle}>
          Pending: {pendingComplaints}
        </div>

        {/* This spacer pushes the remaining content to the right */}
        <div className="flex-grow-1"></div>
      </div>

      <div
        className="second-row d-flex align-items-center mb-3"
        style={{ gap: "12px", paddingLeft: 30 }}
      >
        <div
          className="action-buttons d-flex align-items-center"
          style={{ gap: "8px" }}
        >
          {/* Search with proper icon positioning */}
          <div className={windowWidth < 768 ? "col-12 mb-2" : "col-md-3"}>
            <form
              className="d-flex align-items-center bg-white rounded-2 shadow-sm"
              style={{ height: "36px" }}
            >
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-grow-1"
                style={{
                  fontSize: "0.8rem",
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  background: "transparent",
                  width: "100%",
                  height: "100%",
                  padding: "0 10px",
                }}
              />
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 10px",
                }}
              >
                <Search size={14} color="#1a7a73" />
              </div>
            </form>
          </div>

          {/* Status dropdown */}
          <div className="filter-dropdown">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                ...formElementStyle,
                width: "148px",
              }}
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Bank dropdown */}
          {(userRole === "SUPER ADMIN" || isAdmin === true) && (
            <div
              className="filter-dropdown"
              style={{ display: isAdmin === true ? "none" : "block" }}
            >
              <select
                className="form-select"
                value={selectedBankId}
                onChange={handleBankChange}
                style={{
                  ...formElementStyle,
                  width: "148px",
                }}
              >
                <option value="">All Banks</option>
                {banksList.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.name || bank.bankName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Branch dropdown */}
          {(userRole === "SUPER ADMIN" || isAdmin === true) && (
  <div className="filter-dropdown">
    <select
      className="form-select"
      value={selectedBranch}
      onChange={handleBranchChange}
      style={{
        ...formElementStyle,
        width: "148px",
      }}
      disabled={!selectedBankId || filteredBranches.length === 0}
    >
      <option value="">All Branches</option>
      {filteredBranches.map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name || branch.branchName}
        </option>
      ))}
    </select>
  </div>
)}
 
        </div>
        <div style={{ flex: 1 }}></div>
        {/* Add download buttons */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            position: "absolute",
            right: "20px",
          }}
        >
          {/* Excel Download Button */}
          <button
            onClick={downloadExcel}
            style={{
              padding: "8px 12px",
              backgroundColor: "#10B981",
              color: "white",
              border: "none",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              fontWeight: "500",
              height: "36px",
              fontSize: "12px",
            }}
            title="Export to Excel"
          >
            <Download size={14} />
            <Sheet size={14} />
          </button>

          {/* PDF Download Button */}
          <button
            onClick={downloadPDF}
            style={{
              padding: "8px 12px",
              backgroundColor: "#EF4444",
              color: "white",
              border: "none",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              fontWeight: "500",
              height: "36px",
              fontSize: "12px",
            }}
            title="Export to PDF"
          >
            <Download size={14} />
            <FileTextIcon size={14} />
          </button>
        </div>
      </div>

      <div className="table-section" style={{ marginTop: "-28px" }}>
        <ExpandableTablegrievancetable
          data={filteredComplaints}
          title=""
          showFilter={false}
          columns={columns}
          numberOfRows={itemsPerPage}
          showExport={false}
          showReset={false}
          onExpand={false}
          showSearch={false}
          isHeaderFixed={true}
          rowCount={rowCount}
          headerTableHeadStyle={{
            background: "#1a7a73",
            color: "#FFFFFF",
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: "12px",
            lineHeight: "20px",
            position: "sticky", // Ensure sticky positioning
            top: 0,
            zIndex: 1000,
          }}
          tableStyle={{
            border: "1px solid #f0f0f0",
            width: "98%",
            fontFamily: "Poppins",
            // marginTop: -30,
            padding: "0px",
          }}
        />
      </div>
    </div>
  );
};

export default GrievanceTable;
